import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import ApartmentIcon from '@material-ui/icons/Apartment';
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined';
import { AssignManagerModal } from '../../../shared/components/group-ui/assign-manager-modal/assign-manager-modal.component';
import { ConfirmModal } from '../../../shared/components/core-ui/confirm-modal/confirm-modal.component';
import { ManagersList } from '../../../shared/components/group-ui/managers-list/managers-list.component';
import { Loader } from '../../../shared/components/core-ui/loader/loader.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  addPropertyManager,
  deletePropertyManager,
  getAllProperties,
  getPropertyDetails
} from '../../../shared/services/data/property.data';
import { PropertyUiModel } from '../../../shared/ui-models/property.ui-model';
import { PropertyUiManagersListModel } from '../../../shared/ui-models/propertyManager.ui.model';
import { getModuleName } from '../../shared/services/property-managers.service';

/**
 * Standalone page for assigning and removing property managers on projects. The same capability
 * lives inside the project edit page; this container exposes it on its own under Operations so the
 * ops team does not have to walk through the whole project form to change an assignment.
 */
export const AssignPropertyManagers: React.FC = () => {
  const [projects, setProjects] = useState<PropertyUiModel[]>([]);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<PropertyUiModel | undefined>();
  const [loadingManagers, setLoadingManagers] = useState<boolean>(false);
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
  const [managerToRemove, setManagerToRemove] = useState<string>('');

  useEffect(() => {
    getAllProperties(getModuleName()).then((response: PropertyUiModel[] | ErrorInfo) => {
      setLoadingProjects(false);
      if (isErrorInfo(response)) {
        birdToast.error('Failed to load the projects list', 'Property Managers');
        return;
      }
      setProjects(response);
    });
  }, []);

  const filteredProjects = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter(
      (project) =>
        (project.name || '').toLowerCase().includes(term) ||
        (project.areaName || '').toLowerCase().includes(term)
    );
  }, [projects, searchTerm]);

  /**
   * Keeps the list row and the selected project in sync after every assignment change, so the
   * counter next to a project never drifts from what the panel shows.
   */
  const applyProjectUpdate = (updatedProject: PropertyUiModel) => {
    setSelectedProject(updatedProject);
    setProjects((currentProjects) =>
      currentProjects.map((project) =>
        project.id === updatedProject.id
          ? { ...project, propertyManagersList: updatedProject.propertyManagersList }
          : project
      )
    );
  };

  /**
   * The list endpoint is a search projection, so the assigned managers are re-read from the
   * project details endpoint whenever a project is opened.
   */
  const handleSelectProject = (project: PropertyUiModel) => {
    setSelectedProject(project);
    if (!project.id) return;
    setLoadingManagers(true);
    getPropertyDetails(project.id, getModuleName()).then(
      (response: PropertyUiModel | ErrorInfo) => {
        setLoadingManagers(false);
        if (isErrorInfo(response)) {
          birdToast.error('Failed to load the assigned property managers', project.name);
          return;
        }
        applyProjectUpdate(response);
      }
    );
  };

  const handleAssignManager = (user: PropertyUiManagersListModel) => {
    if (!selectedProject?.id) return;
    const alreadyAssigned = (selectedProject.propertyManagersList || []).some(
      (manager) => manager.id === user.id
    );
    if (alreadyAssigned) {
      birdToast.warning(
        `${user.name} is already assigned to ${selectedProject.name}`,
        'Property Managers'
      );
      return;
    }
    const promise = addPropertyManager(user.id, selectedProject.id, getModuleName()).then(
      (response: PropertyUiModel | ErrorInfo) => {
        if (isErrorInfo(response)) throw response;
        applyProjectUpdate(response);
        return response;
      }
    );
    birdToast.promise(promise, {
      title: 'Property Managers',
      loadingMessage: `assigning ${user.name}...`,
      successMessage: `${user.name} is now a property manager on ${selectedProject.name}`,
      errorFailedMessage: 'failed to assign the property manager',
      errorPermissionMessage: 'not authorized to perform this action'
    });
  };

  const handleRemoveManager = (managerId: string) => {
    if (!selectedProject?.id) return;
    const manager = (selectedProject.propertyManagersList || []).find(
      (item) => item.id === managerId
    );
    const promise = deletePropertyManager(managerId, selectedProject.id, getModuleName()).then(
      (response: PropertyUiModel | ErrorInfo) => {
        if (isErrorInfo(response)) throw response;
        applyProjectUpdate(response);
        return response;
      }
    );
    birdToast.promise(promise, {
      title: 'Property Managers',
      loadingMessage: `removing ${manager?.name || 'the property manager'}...`,
      successMessage: `${manager?.name || 'The property manager'} was removed from ${
        selectedProject.name
      }`,
      errorFailedMessage: 'failed to remove the property manager',
      errorPermissionMessage: 'not authorized to perform this action'
    });
  };

  const renderProjectsList = () => {
    if (loadingProjects) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader />
        </div>
      );
    }

    if (filteredProjects.length === 0) {
      return (
        <div className="px-4 py-12 text-center text-gray-500">
          {projects.length === 0 ? 'No projects found' : 'No project matches your search'}
        </div>
      );
    }

    return (
      <ul className="p-0 m-0 space-y-1 list-none">
        {filteredProjects.map((project) => {
          const isSelected = project.id === selectedProject?.id;
          const managersCount = project.propertyManagersList?.length || 0;
          return (
            <li key={project.id}>
              <button
                type="button"
                onClick={() => handleSelectProject(project)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-colors duration-200 ${
                  isSelected
                    ? 'bg-primary-50 border border-primary-100'
                    : 'border border-transparent hover:bg-gray-50'
                }`}
              >
                <ApartmentIcon
                  className={isSelected ? 'text-primary-500' : 'text-gray-400'}
                  fontSize="small"
                />
                <span className="flex-1 min-w-0">
                  <span
                    className={`block truncate text-lg font-medium ${
                      isSelected ? 'text-primary-700' : 'text-gray-800'
                    }`}
                  >
                    {project.name || 'Untitled project'}
                  </span>
                  {project.areaName && (
                    <span className="block truncate text-base text-gray-500">
                      {project.areaName}
                    </span>
                  )}
                </span>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-sm font-semibold ${
                    managersCount > 0
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {managersCount}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderManagersPanel = () => {
    if (!selectedProject) {
      return (
        <div className="flex flex-col items-center justify-center flex-grow py-20 px-4 text-center border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/50">
          <div className="w-16 h-16 mb-4 rounded-full bg-primary-50 flex items-center justify-center text-primary-500">
            <PersonAddOutlinedIcon style={{ fontSize: 32 }} />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Pick a project</h3>
          <p className="text-gray-500 max-w-sm">
            Select a project from the list to see who manages it and to assign or remove property
            managers.
          </p>
        </div>
      );
    }

    if (loadingManagers) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader />
        </div>
      );
    }

    return (
      <ManagersList
        data={{ propertyManagersList: selectedProject.propertyManagersList || [] }}
        eventHandlers={{
          deletePropertyManager: (id: string) => setManagerToRemove(id),
          addPropertyManager: () => setShowAssignModal(true)
        }}
      />
    );
  };

  return (
    <div className="min-h-screen bg-creams-50 px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] pb-16">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 m-0">Property Managers</h1>
          <p className="text-gray-500 mt-1 mb-0">
            Assign or remove the property managers responsible for each project.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-4 lg:col-span-1 h-fit">
            <div className="flex items-center gap-2 px-3 py-2 mb-3 border border-gray-200 rounded-lg focus-within:border-primary-300">
              <SearchIcon className="text-gray-400" fontSize="small" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by project or area"
                className="flex-1 min-w-0 border-0 outline-none text-base text-gray-700 bg-transparent"
              />
            </div>
            <div className="max-h-[60vh] overflow-y-auto">{renderProjectsList()}</div>
          </div>

          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 lg:col-span-2 flex flex-col min-h-[400px]">
            <div className="flex flex-wrap items-baseline justify-between gap-2 pb-4 border-b border-gray-100">
              <div>
                <div className="text-xl font-bold text-gray-800">
                  {selectedProject?.name || 'No project selected'}
                </div>
                {selectedProject?.areaName && (
                  <div className="text-base text-gray-500">{selectedProject.areaName}</div>
                )}
              </div>
              {selectedProject && (
                <div className="text-base text-gray-500">
                  {selectedProject.propertyManagersList?.length || 0} assigned
                </div>
              )}
            </div>
            {renderManagersPanel()}
          </div>
        </div>
      </div>

      {showAssignModal && (
        <AssignManagerModal
          config={{ showModal: showAssignModal, moduleName: getModuleName() }}
          eventHandlers={{
            closeHandler: () => setShowAssignModal(false),
            chooseUser: (user: PropertyUiManagersListModel) => {
              setShowAssignModal(false);
              handleAssignManager(user);
            }
          }}
        />
      )}

      <ConfirmModal
        config={{
          showModal: !!managerToRemove,
          message: 'Are you sure you want to remove this property manager from the project?'
        }}
        eventHandlers={{
          denyAction: () => setManagerToRemove(''),
          confirmAction: () => {
            handleRemoveManager(managerToRemove);
            setManagerToRemove('');
          }
        }}
      />
    </div>
  );
};

export default AssignPropertyManagers;
